import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Sparkles, Repeat, TrendingUp, TrendingDown, Bot } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

// Hoisted stable style objects (avoid new refs on every render)
const AXIS_TICK = { fontSize: 11, fill: "#5F7368" };
const TOOLTIP_CONTENT_STYLE = { background: "#fff", border: "1px solid #EBEBEA", borderRadius: 12 };
const BAR_CURSOR = { fill: "#EBEBEA", opacity: 0.4 };
const LEGEND_STYLE = { fontSize: 11 };
const BAR_RADIUS = [4, 4, 0, 0];

const CATEGORY_LABELS = {
  nomina: "Nómina", hipoteca: "Hipoteca", alquiler: "Alquiler", servicios: "Servicios",
  supermercado: "Supermercado", restaurantes: "Restaurantes", transporte: "Transporte",
  gasolina: "Gasolina", salud: "Salud", ocio: "Ocio", viajes: "Viajes", compras: "Compras",
  educacion: "Educación", impuestos: "Impuestos", transferencias: "Transferencias",
  ingresos: "Otros ingresos", cripto: "Cripto", inversiones: "Inversiones", otros: "Otros",
};

const CAT_COLORS = {
  hipoteca: "#0F291E", alquiler: "#3C6A55", servicios: "#1C7C54", supermercado: "#4B9471",
  restaurantes: "#D95D39", transporte: "#8EBFA6", gasolina: "#E58B6E", ocio: "#75A891",
  viajes: "#2E5A45", compras: "#B8CFC4", educacion: "#93B5A5", impuestos: "#6B8E80",
  salud: "#4A7A63", transferencias: "#89AA9A", otros: "#B0C7BB", nomina: "#1C7C54",
  ingresos: "#4B9471", cripto: "#0F291E", inversiones: "#1C7C54",
};

function fmtMonth(m, lang) {
  if (!m) return "";
  const [y, mm] = m.split("-");
  const namesEs = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const namesEn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const names = lang === "es" ? namesEs : namesEn;
  return `${names[parseInt(mm,10)-1]} ${y.slice(2)}`;
}

export function MonthlyExpensesCard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const currency = user?.base_currency || "USD";
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  useEffect(() => {
    api.get("/analytics/monthly-expenses")
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], categories: [], variation: {} }));
  }, []);

  if (!data) return null;
  const { rows, categories, variation } = data;
  const variationEntries = Object.entries(variation || {})
    .sort((a, b) => Math.abs((b[1].delta_pct ?? 0)) - Math.abs((a[1].delta_pct ?? 0)))
    .slice(0, 6);

  return (
    <div className="card-surface p-6" data-testid="monthly-expenses-card">
      <div className="mb-4">
        <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("analytics.movements", { defaultValue: "Transactions" })}</p>
        <h3 className="font-display text-lg font-semibold text-[#0A110E]">{t("analytics.monthlyExpensesTitle", { defaultValue: "Monthly expenses by category" })}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#8A9992]">
          {t("analytics.importFirst", { defaultValue: "Import your bank statement to see your expenses month by month." })}
        </div>
      ) : (
        <>
          <div className="h-72 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows.map((r) => ({ ...r, month: fmtMonth(r.month, lang) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEA" vertical={false} />
                <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis
                  tick={AXIS_TICK}
                  axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v) => formatCurrency(v, currency).replace(/\.00$/, "")}
                />
                <Tooltip
                  formatter={(v, name) => [formatCurrency(v, currency), CATEGORY_LABELS[name] || name]}
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  cursor={BAR_CURSOR}
                />
                <Legend wrapperStyle={LEGEND_STYLE} formatter={(v) => CATEGORY_LABELS[v] || v} />
                {categories.map((c) => (
                  <Bar key={c} dataKey={c} stackId="a" fill={CAT_COLORS[c] || "#8EBFA6"} radius={BAR_RADIUS} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {variationEntries.length > 0 && (
            <div className="mt-6 border-t border-[#EBEBEA] pt-4">
              <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] mb-3">
                {t("analytics.variationLabel", { defaultValue: "Previous month → last month" })}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {variationEntries.map(([cat, v]) => {
                  const up = (v.delta ?? 0) > 0;
                  const changed = v.delta_pct !== null && Math.abs(v.delta_pct) >= 1;
                  return (
                    <div key={cat} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-[#0A110E]">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CAT_COLORS[cat] || "#8EBFA6" }} />
                        {CATEGORY_LABELS[cat] || cat}
                      </span>
                      <span className="flex items-center gap-2 tabular-nums">
                        <span className="text-[#5F7368]">{formatCurrency(v.curr, currency)}</span>
                        {changed && v.delta_pct !== null && (
                          <span
                            className={`text-xs font-semibold flex items-center gap-0.5 ${
                              up ? "text-[#D95D39]" : "text-[#1C7C54]"
                            }`}
                          >
                            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {up ? "+" : ""}{v.delta_pct}%
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function RecurringCard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const currency = user?.base_currency || "USD";

  useEffect(() => {
    api.get("/analytics/recurring").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items === null) return null;

  return (
    <div className="card-surface p-6" data-testid="recurring-card">
      <div className="mb-4 flex items-center gap-2">
        <Repeat className="w-4 h-4 text-[#1C7C54]" />
        <div>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("analytics.recurringKicker", { defaultValue: "Recurring expenses" })}</p>
          <h3 className="font-display text-lg font-semibold text-[#0A110E]">{t("analytics.recurringTitle", { defaultValue: "Subscriptions and fixed charges" })}</h3>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A9992]">
          {t("analytics.recurringEmpty", { defaultValue: "They are detected when the same charge appears in 2+ months. Import more statements to see them." })}
        </div>
      ) : (
        <ul className="divide-y divide-[#EBEBEA]" data-testid="recurring-list">
          {items.slice(0, 8).map((r) => {
            const changed = Math.abs(r.delta_pct) >= 5;
            const up = r.delta_pct > 0;
            return (
              <li key={r.key} className="py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0A110E] truncate">{r.example_description}</div>
                  <div className="text-xs text-[#8A9992] flex items-center gap-2">
                    <span className="uppercase tracking-wider">{CATEGORY_LABELS[r.category] || r.category}</span>
                    <span>·</span>
                    <span>{r.months} {t("analytics.monthsShort", { defaultValue: "months" })}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums text-[#D95D39]">
                    {formatCurrency(r.last_amount, currency)}
                  </div>
                  {changed ? (
                    <div className={`text-xs tabular-nums flex items-center justify-end gap-1 ${up ? "text-[#D95D39]" : "text-[#1C7C54]"}`}>
                      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {r.delta_pct > 0 ? "+" : ""}{r.delta_pct}% {t("analytics.vsAvg", { defaultValue: "vs avg" })}
                    </div>
                  ) : (
                    <div className="text-xs text-[#8A9992] tabular-nums">{t("analytics.stable", { defaultValue: "stable" })}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function InsightCard() {
  const { t } = useTranslation();
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/insight")
      .then((r) => setText(r.data?.insight))
      .catch(() => setText(null))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && !text) return null;

  return (
    <div className="card-surface p-6 relative overflow-hidden" data-testid="insight-card">
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#1C7C54]/5 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center">
            <Bot className="w-4 h-4 text-[#1C7C54]" />
          </div>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("analytics.insightKicker", { defaultValue: "AI insight" })}</p>
          <Sparkles className="w-3 h-3 text-[#1C7C54]" />
        </div>
        {loading ? (
          <div className="text-sm text-[#8A9992]">{t("analytics.insightLoading", { defaultValue: "Analyzing your transactions…" })}</div>
        ) : (
          <p className="font-display text-[15px] leading-relaxed text-[#0A110E]">{text}</p>
        )}
      </div>
    </div>
  );
}
